import { ApiResult } from "common/models";
import { HttpClient } from "common/services/http-client.service";
import { withApiResponse } from "common/utils/rtk-query.util";
import { mapKnowledgeGraphResponse } from "modules/processing-hub/data/utils/knowledge-graph.util";
import { mocApiConfig } from "../config/moc-api.config";
import { MocKpi, MocPriority, MocRow } from "../moc.data";
import {
  MocDetail,
  MocEngineeringChangeLog,
  MocListResponse,
  MocListView,
  MocMutationResponse,
  MocRequest,
  LinkMocDocumentsRequest,
  LinkMocDocumentsResponse,
  EngineeringAnalysisResponse,
  EngineeringAnalysisView,
  ImpactAssessmentResponse,
  ImpactAssessmentView,
  RiskAssessmentResponse,
  RiskAssessmentView,
  ActionRegisterResponse,
  ActionRegisterView,
  SafeguardResponse,
  SafeguardView,
  AssessmentType,
  AssessmentStatus,
  AssessmentStatusUpdateResponse,
  GenerateMocReportResponse,
  MocDocumentAccessDetails,
  DecisionPackageView,
  UpdateDecisionSummaryRequest,
  UpdateDecisionSummaryResponse,
  MocHistoryResponse,
  MocHistoryView,
  MocHistoryItemDto,
  MocHistoryItem
} from "../models/moc.model";
export class MocService extends HttpClient {
  public static getMocs() {
    return withApiResponse<MocListView>(async () => {
      const response = await this.invokeApi<MocListResponse>(
        mocApiConfig.getMocs()
      );
      return new ApiResult<MocListView>(
        this.mapToView(response.value),
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getMocById(mocId: string) {
    return withApiResponse<MocDetail>(async () => {
      const response = await this.invokeApi<MocDetail>(
        mocApiConfig.getMocById(mocId)
      );
      return new ApiResult<MocDetail>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getMocEngineeringAnalysis(mocId: string) {
    return withApiResponse<EngineeringAnalysisView>(async () => {
      const response = await this.invokeApi<EngineeringAnalysisResponse>(
        mocApiConfig.getMocEngineeringAnalysis(mocId)
      );
      return new ApiResult<EngineeringAnalysisView>(
        this.mapEngineeringAnalysis(response.value),
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getMocImpactAssessment(mocId: string) {
    return withApiResponse<ImpactAssessmentView>(async () => {
      const response = await this.invokeApi<ImpactAssessmentResponse>(
        mocApiConfig.getMocImpactAssessment(mocId)
      );
      return new ApiResult<ImpactAssessmentView>(
        this.mapImpactAssessment(response.value),
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getMocRiskAssessment(mocId: string) {
    return withApiResponse<RiskAssessmentView>(async () => {
      const response = await this.invokeApi<RiskAssessmentResponse>(
        mocApiConfig.getMocRiskAssessment(mocId)
      );
      return new ApiResult<RiskAssessmentView>(
        this.mapRiskAssessment(response.value),
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getMocActionRegister(mocId: string) {
    return withApiResponse<ActionRegisterView>(async () => {
      const response = await this.invokeApi<ActionRegisterResponse>(
        mocApiConfig.getMocActionRegister(mocId)
      );
      return new ApiResult<ActionRegisterView>(
        this.mapActionRegister(response.value),
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getMocSafeguards(mocId: string) {
    return withApiResponse<SafeguardView>(async () => {
      const response = await this.invokeApi<SafeguardResponse>(
        mocApiConfig.getMocSafeguards(mocId)
      );
      return new ApiResult<SafeguardView>(
        this.mapSafeguards(response.value),
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static createMoc(payload: MocRequest) {
    return withApiResponse<MocMutationResponse>(async () => {
      const response = await this.invokeApi<MocMutationResponse>(
        mocApiConfig.createMoc(payload)
      );
      return new ApiResult<MocMutationResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static updateAssessmentStatus(
    assessmentType: AssessmentType,
    assessmentId: string,
    status: AssessmentStatus
  ) {
    return withApiResponse<AssessmentStatusUpdateResponse>(async () => {
      const response = await this.invokeApi<AssessmentStatusUpdateResponse>(
        mocApiConfig.updateAssessmentStatus(
          assessmentType,
          assessmentId,
          status
        )
      );
      return new ApiResult<AssessmentStatusUpdateResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static updateMoc(mocId: string, payload: MocRequest) {
    return withApiResponse<MocMutationResponse>(async () => {
      const response = await this.invokeApi<MocMutationResponse>(
        mocApiConfig.updateMoc(mocId, payload)
      );
      return new ApiResult<MocMutationResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static addMocDocuments(
    mocId: string,
    payload: LinkMocDocumentsRequest
  ) {
    return withApiResponse<LinkMocDocumentsResponse>(async () => {
      const response = await this.invokeApi<LinkMocDocumentsResponse>(
        mocApiConfig.addMocDocuments(mocId, payload)
      );
      return new ApiResult<LinkMocDocumentsResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static generateFinalMocReport(mocId: string, file: Blob) {
    return withApiResponse<GenerateMocReportResponse>(async () => {
      const response = await this.invokeApi<GenerateMocReportResponse>(
        mocApiConfig.generateFinalMocReport(mocId, file)
      );
      return new ApiResult<GenerateMocReportResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getMocDocumentAccessDetails(mocId: string) {
    return withApiResponse<MocDocumentAccessDetails>(async () => {
      const response = await this.invokeApi<MocDocumentAccessDetails>(
        mocApiConfig.getMocDocumentAccessDetails(mocId)
      );
      return new ApiResult<MocDocumentAccessDetails>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getMocDecisionPackage(mocId: string) {
    return withApiResponse<DecisionPackageView>(async () => {
      const response = await this.invokeApi<DecisionPackageView>(
        mocApiConfig.getMocDecisionPackage(mocId)
      );
      return new ApiResult<DecisionPackageView>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static updateDecisionSummary(
    mocId: string,
    payload: UpdateDecisionSummaryRequest
  ) {
    return withApiResponse<UpdateDecisionSummaryResponse>(async () => {
      const response = await this.invokeApi<UpdateDecisionSummaryResponse>(
        mocApiConfig.updateDecisionSummary(mocId, payload)
      );
      return new ApiResult<UpdateDecisionSummaryResponse>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  public static getMocHistory(mocId: string) {
    return withApiResponse<MocHistoryView>(async () => {
      const response = await this.invokeApi<MocHistoryResponse>(
        mocApiConfig.getMocHistory(mocId)
      );
      return new ApiResult<MocHistoryView>(
        this.mapHistory(response.value),
        response.statusCode,
        ...response.errors
      );
    });
  }

  private static mapHistory(data?: MocHistoryResponse): MocHistoryView {
    if (!data) {
      return { mocId: "", totalRecords: 0, items: [] };
    }
    return {
      mocId: data.moc_id ?? "",
      totalRecords: data.total_records ?? 0,
      items: (data.items || []).map((item, index) =>
        this.mapHistoryItem(item, index)
      )
    };
  }

  private static mapHistoryItem(
    item: MocHistoryItemDto,
    index: number
  ): MocHistoryItem {
    return {
      id: `${item.new_stage || "stage"}-${index}`,
      stage: item.new_stage ?? "",
      stageLabel: this.formatStage(item.new_stage),
      status: item.new_status ?? "",
      comments: item.comments ?? "",
      actionBy: item.action_by ?? "",
      actionByName: this.formatActionBy(item.action_by),
      actionOn: item.action_on ?? "",
      date: this.formatHistoryDate(item.action_on),
      time: this.formatHistoryTime(item.action_on)
    };
  }

  // Turns an email or system identifier into a readable name.
  private static formatActionBy(actionBy?: string): string {
    if (!actionBy) {
      return "";
    }
    if (!actionBy.includes("@")) {
      return actionBy;
    }
    const localPart = actionBy.split("@")[0];
    return localPart
      .split(/[._-]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  private static toLocalDate(value?: string): Date | null {
    if (!value) {
      return null;
    }
    let normalized = value.replace(/(\.\d{3})\d+$/, "$1");
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized)) {
      normalized = `${normalized}Z`;
    }
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private static formatHistoryDate(value?: string): string {
    const date = this.toLocalDate(value);
    if (!date) {
      return value ?? "";
    }
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  }

  private static formatHistoryTime(value?: string): string {
    const date = this.toLocalDate(value);
    if (!date) {
      return "";
    }
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit"
    });
  }

  private static mapEngineeringAnalysis(
    data?: EngineeringAnalysisResponse
  ): EngineeringAnalysisView {
    const affectedSystems = (data?.analysis?.affected_systems || []).map(
      (system, index) => ({
        id: `${system.name || "system"}-${index}`,
        label: system.label || "",
        name: system.name || "",
        relatedTo: system.related_to || "",
        impact: system.impact || ""
      })
    );

    const linkedReferences = (data?.analysis?.linked_references || []).map(
      (reference) => ({
        documentId: reference.document_id || "",
        documentName: reference.document_name || "",
        type: reference.type || ""
      })
    );

    const knowledgeGraph = this.mapAnalysisKnowledgeGraph(
      data,
      affectedSystems
    );

    return {
      resultId: data?.result_id ?? "",
      mocId: data?.moc_id ?? "",
      summary: data?.summary ?? "",
      confidenceScore: data?.confidence_score ?? null,
      isMocRequired: data?.is_moc_required ?? false,
      status: data?.status ?? "",
      affectedSystems,
      linkedReferences,
      recommendation: {
        mocRequired: data?.recommendation?.moc_required ?? false,
        reasoning: data?.recommendation?.reasoning || [],
        confidence: data?.recommendation?.recommendation_confidence ?? null
      },
      knowledgeGraph
    };
  }

  // Builds the render-ready graph and flags nodes that match affected systems.
  private static mapAnalysisKnowledgeGraph(
    data: EngineeringAnalysisResponse | undefined,
    affectedSystems: EngineeringAnalysisView["affectedSystems"]
  ): EngineeringAnalysisView["knowledgeGraph"] {
    const graph = data?.knowledge_graph;
    if (!graph || !(graph.nodes?.length ?? 0)) {
      return null;
    }
    const { nodes, edges } = mapKnowledgeGraphResponse(graph);
    const affectedNames = new Set(
      affectedSystems
        .map((system) => (system.name || "").trim().toLowerCase())
        .filter(Boolean)
    );
    const affectedNodeIds = (graph.nodes || [])
      .filter((node) =>
        affectedNames.has((node.assetName || "").trim().toLowerCase())
      )
      .map((node) => node.id);
    return {
      nodes,
      edges,
      affectedNodeIds,
      documentGuidCode: graph.documentGuidCode || ""
    };
  }

  private static mapImpactAssessment(
    data?: ImpactAssessmentResponse
  ): ImpactAssessmentView {
    const items = (data?.items || []).map((item) => ({
      impactId: item.impact_id || "",
      category: item.impact_category || "",
      description: item.impact_description || "",
      severity: item.severity || "",
      owner: item.owner || "",
      status: item.status || "",
      comments: item.comments || "",
      rationale: item.rationale || "",
      evidence: item.evidence || [],
      confidenceScore: item.confidence_score ?? null
    }));

    return {
      mocId: data?.moc_id ?? "",
      totalRecords: data?.total_records ?? 0,
      engineeringConfidenceScore: data?.engineering_confidence_score ?? null,
      items
    };
  }

  private static mapRiskAssessment(
    data?: RiskAssessmentResponse
  ): RiskAssessmentView {
    const items = (data?.items || []).map((item) => ({
      riskId: item.risk_id || "",
      category: item.risk_category || "",
      description: item.risk_description || "",
      likelihood: item.likelihood ?? null,
      severity:
        item.severity ??
        (typeof item.consequence === "number" ? item.consequence : null),
      riskScore: item.risk_score ?? null,
      riskRating: item.risk_rating || "",
      mitigationPlan: item.mitigation_plan || "",
      owner: item.owner || "",
      status: item.status || "",
      comments: item.comments || "",
      consequenceDescription:
        typeof item.consequence === "string"
          ? item.consequence
          : item.consequence_description || "",
      existingSafeguards: item.existing_safeguards || [],
      initialRisk: item.initial_risk || "",
      residualRisk: item.residual_risk || "",
      confidenceScore: item.confidence_score ?? null
    }));

    return {
      mocId: data?.moc_id ?? "",
      totalRecords: data?.total_records ?? 0,
      items
    };
  }

  private static mapActionRegister(
    data?: ActionRegisterResponse
  ): ActionRegisterView {
    const items = (data?.items || []).map((item) => ({
      actionId: item.action_id || "",
      actionCode: item.action_code || "",
      description: item.action_description || "",
      category: item.action_category || "",
      owner: item.owner || "",
      priority: item.priority || "",
      status: item.status || "",
      targetDate: item.target_date ?? null,
      completionDate: item.completion_date ?? null,
      comments: item.comments || "",
      dueStage: item.due_stage || ""
    }));

    return {
      mocId: data?.moc_id ?? "",
      totalRecords: data?.total_records ?? 0,
      items
    };
  }

  private static mapSafeguards(data?: SafeguardResponse): SafeguardView {
    const items = (data?.items || []).map((item) => ({
      safeguardId: item.safeguard_id || "",
      impactHazardControlled: item.impact_controlled || "",
      safeguard: item.safeguard_description || "",
      criticality: item.criticality || "",
      owner: item.owner || "",
      status: item.engineer_decision || ""
    }));

    return {
      mocId: data?.moc_id ?? "",
      totalRecords: data?.total_records ?? 0,
      items
    };
  }

  private static mapToView(data?: MocListResponse): MocListView {
    if (!data) {
      return { kpis: [], rows: [], totalRecords: 0 };
    }

    return {
      kpis: this.mapKpis(data),
      rows: (data.engineeringChangeLogs || []).map((log, index) =>
        this.mapRow(log, index)
      ),
      totalRecords: data.totalRecords ?? 0
    };
  }

  private static mapKpis(data: MocListResponse): MocKpi[] {
    const summary = data.summary;
    return [
      {
        id: "active",
        label: "Active MOC",
        value: String(summary?.activeMoc ?? 0),
        accent: "default"
      },
      {
        id: "draft",
        label: "Draft",
        value: String(summary?.draft ?? 0),
        accent: "default"
      },
      {
        id: "awaiting",
        label: "Awaiting Approval",
        value: String(summary?.awaitingApproval ?? 0),
        accent: "orange"
      },
      {
        id: "completed",
        label: "Completed",
        value: String(summary?.completed ?? 0),
        accent: "green"
      }
    ];
  }

  private static mapRow(log: MocEngineeringChangeLog, index: number): MocRow {
    return {
      id: `${log.mocId}-${index}`,
      mocId: log.mocId,
      title: log.title,
      asset: log.asset,
      owner: log.owner,
      stage: this.formatStage(log.workflowStage),
      workflowStage: log.workflowStage ?? "",
      priority: this.formatPriority(log.priority),
      finImpact: log.financialImpact,
      schImpact: log.scheduleImpact,
      lastUpdated: this.formatDateTime(log.lastUpdated)
    };
  }

  private static formatStage(stage?: string): string {
    if (!stage) {
      return "";
    }
    return stage
      .toLowerCase()
      .split(/[_\s]+/)
      .filter(Boolean)
      .map((word) =>
        word === "ai" ? "AI" : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join(" ");
  }

  private static formatPriority(priority?: string): MocPriority {
    const normalized = (priority || "").toUpperCase();
    if (normalized === "HIGH" || normalized === "LOW") {
      return normalized;
    }
    return "MEDIUM";
  }

  private static formatDateTime(value?: string): string {
    if (!value) {
      return "";
    }
    // Backend sends timezone-less ISO with microseconds (e.g. 2026-08-24T10:10:01.947533);
    // trim to milliseconds and treat it as UTC so it converts to the viewer's local zone.
    let normalized = value.replace(/(\.\d{3})\d+$/, "$1");
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized)) {
      normalized = `${normalized}Z`;
    }
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  }
}
